import { useEffect, useLayoutEffect, useState } from "react";
import {
  COLOR_BLIND_MODE_ATTRIBUTE,
  COLOR_BLIND_MODE_CLASS_BY_MODE,
  COLOR_BLIND_MODE_CLASSES,
  COLOR_BLIND_MODE_STORAGE_KEY,
  COLOR_BLIND_MODE_VALUES,
  ColorBlindModes,
  DARK_CLASS_NAME,
  ThemeModes,
  THEME_STORAGE_KEY
} from "./constants";
import { colors } from "../../utils/colors"; 
// ============================================================================
// BROWSER ENVIRONMENT CHECKS
// ============================================================================

const isBrowser = typeof window !== "undefined";
const hasDocument = typeof document !== "undefined";
const hasLocalStorage = isBrowser && window.localStorage;
const hasMatchMedia = isBrowser && typeof window.matchMedia === "function";
const NOOP_DISCONNECT = Object.freeze({ disconnect: () => {} });

// ============================================================================
// THEME DETECTION
// ============================================================================

/**
 * Checks if dark mode is currently active in the DOM
 * Use this to get current theme state anywhere in the app
 * @returns {boolean} True if dark mode is active
 */
export const isDarkModeActive = () => {
  if (!hasDocument) return false;
  return (
    document.documentElement.classList.contains(DARK_CLASS_NAME) ||
    document.body.classList.contains(DARK_CLASS_NAME)
  );
};

/**
 * Gets the current theme mode from DOM state
 * @returns {string} Current theme mode ('light' or 'dark')
 */
export const getCurrentTheme = () => {
  return isDarkModeActive() ? ThemeModes.DARK : ThemeModes.LIGHT;
};

/**
 * Checks if the given theme mode is dark (internal helper)
 * @private
 */
const isDarkMode = (theme) => theme === ThemeModes.DARK;

/**
 * Resolves the current machine/OS theme preference
 * @returns {string} Resolved theme mode ('light' or 'dark')
 */
export const getSystemTheme = () => {
  if (!hasMatchMedia) return ThemeModes.LIGHT;
  return window.matchMedia("(prefers-color-scheme: dark)").matches
    ? ThemeModes.DARK
    : ThemeModes.LIGHT;
};

// ============================================================================
// THEME CHANGE OBSERVER
// ============================================================================

/**
 * MutationObserver that watches for theme/color mode changes and executes a callback.
 * Useful for components that need to react to theme changes (e.g., amCharts).
 * 
 * @param {Function} onThemeChange - Callback function executed when theme changes
 * @returns {{ disconnect: Function }} Object with disconnect method to cleanup observer
 */
export const createThemeChangeObserver = (onThemeChange) => {
  if (!hasDocument) {
    return { disconnect: () => {} };
  }

  const observer = new MutationObserver((mutations) => {
    const hasThemeChange = mutations.some(
      (mutation) =>
        mutation.attributeName === "class" ||
        mutation.attributeName === COLOR_BLIND_MODE_ATTRIBUTE
    );
    if (hasThemeChange) {
      onThemeChange();
    }
  });

  observer.observe(document.documentElement, {
    attributes: true,
    attributeFilter: ["class"]
  });

  if (document.body) {
    observer.observe(document.body, {
      attributes: true,
      attributeFilter: [COLOR_BLIND_MODE_ATTRIBUTE]
    });
  }

  return {
    disconnect: () => observer.disconnect()
  };
};

/**
 * MediaQuery observer that watches machine/OS theme changes.
 * Useful when theme preference is set to 'system'.
 *
 * @param {Function} onThemeChange - Callback function executed when system theme changes
 * @returns {{ disconnect: Function }} Object with disconnect method to cleanup observer
 */
export const createSystemThemeObserver = (onThemeChange) => {
  if (!hasMatchMedia) {
    return { disconnect: () => {} };
  }

  const mediaQuery = window.matchMedia("(prefers-color-scheme: dark)");
  const handler = () => onThemeChange();

  if (typeof mediaQuery.addEventListener === "function") {
    mediaQuery.addEventListener("change", handler);
    return {
      disconnect: () => mediaQuery.removeEventListener("change", handler)
    };
  }

  mediaQuery.addListener(handler);
  return {
    disconnect: () => mediaQuery.removeListener(handler)
  };
};
/** Gets the appropriate header class name based on whether dark mode is active.
 * @param {boolean} isDarkTheme - Whether dark mode is active
 * @returns {string} Header class name
 */
export const getDarkModeHeaderClassName = (isDarkTheme) => {
  return isDarkTheme ? "header dark-mode-report-header" : "header";
};

/** Custom hook to get the appropriate header class name based on the current theme.
 * @returns {string} Header class name
 */
export const useHeaderClassNameByTheme = () => {
  const [isDarkTheme, setIsDarkTheme] = useState(() => getCurrentTheme() === ThemeModes.DARK);

  useEffect(() => {
    const observer = createThemeChangeObserver(() => {
      setIsDarkTheme(getCurrentTheme() === ThemeModes.DARK);
    });

    return () => observer.disconnect();
  }, []);

  return getDarkModeHeaderClassName(isDarkTheme);
};

/** Custom hook for workflow pages to react to theme changes and initialize
 * DOM theme from persisted preference.
 * @returns {boolean} True when dark theme is active
 */
export const useWorkflowThemeMode = () => {
  const [isDarkTheme, setIsDarkTheme] = useState(() => getCurrentTheme() === ThemeModes.DARK);

  useEffect(() => {
    const storedTheme = normalizeThemeMode(readStoredTheme(THEME_STORAGE_KEY));
    applyTheme(storedTheme);

    if (storedTheme !== ThemeModes.SYSTEM) {
      return undefined;
    }
    const systemObserver = createSystemThemeObserver(() => {
      applyTheme(ThemeModes.SYSTEM);
    });

    return () => systemObserver.disconnect();
  }, []);

  useEffect(() => {
    const syncTheme = () => {
      setIsDarkTheme(getCurrentTheme() === ThemeModes.DARK);
    };

    syncTheme();
    const observer = createThemeChangeObserver(syncTheme);
    window.addEventListener("storage", syncTheme);

    return () => {
      observer.disconnect();
      window.removeEventListener("storage", syncTheme);
    };
  }, []);

  return isDarkTheme;
};

// ============================================================================
// THEME STORAGE
// ============================================================================

/**
 * Validates and normalizes theme mode
 * @param {string} mode - Theme mode to validate
 * @returns {string} Valid theme mode (defaults to light)
 */
export const normalizeThemeMode = (mode) => {
  const validModes = Object.values(ThemeModes);
  return validModes.includes(mode) ? mode : ThemeModes.LIGHT;
};

/**
 * Validates and normalizes vManage colorblind mode values.
 * @param {string} mode - Colorblind mode to validate
 * @returns {string} Valid colorblind mode (defaults to off)
 */
export const normalizeColorBlindMode = (mode) => {
  return COLOR_BLIND_MODE_VALUES.includes(mode) ? mode : ColorBlindModes.OFF;
};

const readLocalStorageValue = (storageKey, warningMessage) => {
  if (!hasLocalStorage) return null;

  try {
    return localStorage.getItem(storageKey);
  } catch {
    console.warn(warningMessage);
    return null;
  }
};

/**
 * Reads theme preference from localStorage
 * @param {string} storageKey - localStorage key
 * @returns {string|null} Stored theme or null
 */
export const readStoredTheme = (storageKey) => {
  return readLocalStorageValue(
    storageKey,
    "Failed to read theme preference from localStorage"
  );
};

/**
 * Reads and normalizes vManage colorblind mode from localStorage.
 * @returns {string} Valid colorblind mode
 */
export const readStoredColorBlindMode = () => {
  return normalizeColorBlindMode(
    readLocalStorageValue(
      COLOR_BLIND_MODE_STORAGE_KEY,
      "Failed to read colorblind mode preference from localStorage"
    )
  );
};

/**
 * Persists theme preference to localStorage
 * @param {string} storageKey - localStorage key
 * @param {string} mode - Theme mode to save
 */
export const setThemePreference = (storageKey, mode) => {
  if (!hasLocalStorage) return;

  try {
    localStorage.setItem(storageKey, mode);
  } catch {
    console.warn("Failed to save theme preference to localStorage");
  }
};

// ============================================================================
// THEME APPLICATION
// ============================================================================

/**
 * Updates DOM classes and attributes for theme
 * @private
 */
const updateDOMAttributes = (preferenceTheme, appliedTheme) => {
  const { documentElement: root, body } = document;
  const isDark = isDarkMode(appliedTheme);

  // Toggle dark mode class
  root.classList.toggle(DARK_CLASS_NAME, isDark);
  body.classList.toggle(DARK_CLASS_NAME, isDark);

  // Set theme attributes
  root.setAttribute("data-theme-preference", preferenceTheme);
  root.setAttribute("data-theme-applied", appliedTheme);
  root.style.colorScheme = appliedTheme;
  body.setAttribute("data-theme-applied", appliedTheme);
};

/**
 * Applies theme to the entire application
 * Updates DOM attributes for theme switching
 * @param {string} theme - Theme mode ('light' or 'dark')
 */
export const applyTheme = (theme) => {
  if (!hasDocument) return;

  const preferenceTheme = normalizeThemeMode(theme);
  const appliedTheme = preferenceTheme === ThemeModes.SYSTEM
    ? getSystemTheme()
    : preferenceTheme;

  updateDOMAttributes(preferenceTheme, appliedTheme);
};

/**
 * Applies a vManage-compatible colorblind mode to the document body.
 * @param {string} mode - Colorblind mode
 */
export const applyColorBlindMode = (mode) => {
  if (!hasDocument || !document.body) return;

  const colorBlindMode = normalizeColorBlindMode(mode);

  document.body.classList.remove(...COLOR_BLIND_MODE_CLASSES);
  document.body.setAttribute(COLOR_BLIND_MODE_ATTRIBUTE, colorBlindMode);

  const colorBlindModeClassName = COLOR_BLIND_MODE_CLASS_BY_MODE[colorBlindMode];
  if (colorBlindModeClassName) {
    document.body.classList.add(colorBlindModeClassName);
  }
};

/**
 * Initializes vManage colorblind mode only for Convergence sessions.
 * @param {boolean} isConvergenceMode - True when app is running in Convergence mode
 * @returns {{ disconnect: Function }} Object with disconnect method to cleanup listeners
 */
export const initializeConvergenceColorBlindMode = (isConvergenceMode) => {
  if (!isConvergenceMode) {
    return NOOP_DISCONNECT;
  }

  applyColorBlindMode(readStoredColorBlindMode());

  if (!isBrowser) {
    return NOOP_DISCONNECT;
  }

  const handleColorBlindStorageChange = (event) => {
    if (
      event.key === COLOR_BLIND_MODE_STORAGE_KEY ||
      event.key === null
    ) {
      applyColorBlindMode(readStoredColorBlindMode());
    }
  };

  window.addEventListener("storage", handleColorBlindStorageChange);

  return {
    disconnect: () =>
      window.removeEventListener("storage", handleColorBlindStorageChange)
  };
};

export const useConvergenceColorBlindMode = (isConvergenceMode) => {
  const isConvergence = Boolean(isConvergenceMode);

  useLayoutEffect(() => {
    const colorBlindMode = initializeConvergenceColorBlindMode(
      isConvergence
    );

    return () => colorBlindMode.disconnect();
  }, [isConvergence]);
};

// ============================================================================
// AMCHARTS TOOLTIP UTILITIES
// ============================================================================

/**
 * Tooltip color configurations for light and dark themes
 * Matches Harbor hbr-tooltip component styling
 * @private
 */
const TOOLTIP_COLORS = Object.freeze({
  light: {
    background: colors.gray100,  // #FFFFFF
    text: colors.gray15,         // #23282E
    border: colors.gray45,       // #656C75
  },
  dark: {
    background: colors.gray15,      // #23282E
    text: colors.gray100,         // #FFFFFF
    border: colors.gray45,     // #656C75
  },
});

/**
 * Gets tooltip colors based on current theme
 * Use for styling custom tooltips (e.g., amCharts) to match Harbor design
 * @returns {{ background: string, text: string, border: string }}
 */
export const getTooltipColors = () => {
  return isDarkModeActive() ? TOOLTIP_COLORS.dark : TOOLTIP_COLORS.light;
};

/**
 * Applies theme-aware colors to an amCharts tooltip.
 * Updates background, text, border colors and shadow opacity based on current theme.
 * 
 * @param {object} tooltip - amCharts tooltip object
 * @param {object} am4core - amCharts core module (for color conversion)
 * @returns {void}
 */
export const applyTooltipTheme = (tooltip, am4core) => {
  if (!tooltip || !am4core) return;
  
  const { background, text, border } = getTooltipColors();
  const isDark = isDarkModeActive();
  
  tooltip.background.fill = am4core.color(background);
  tooltip.label.fill = am4core.color(text);
  tooltip.background.stroke = am4core.color(border);
  
  const shadow = tooltip.background.filters.getIndex(0);
  if (shadow) {
    shadow.opacity = isDark ? 0.4 : 0.18;
  }
};

// ============================================================================
// AMCHARTS LABEL & LEGEND UTILITIES
// ============================================================================

/**
 * Safely attaches an event handler to an amCharts object.
 * Guards against attaching to null, disposed, or events-less targets.
 *
 * @param {object} target - amCharts object to attach the event to
 * @param {string} eventName - Name of the event (e.g., 'focus', 'blur', 'disposed')
 * @param {Function} handler - Callback to invoke when the event fires
 * @returns {object|null} The event disposer returned by `events.on`, or null if attachment was skipped
 */
const safeOn = (target, eventName, handler) => {
  if (!target?.events || target.isDisposed?.()) return null;
  try {
    return target.events.on(eventName, handler);
  } catch {
    return null;
  }
};

/**
 * Repaints already-rendered amCharts legend items with the current theme colors.
 * Updates both template-level styling (applied to future items) and any items that
 * have already been rendered — which is necessary after interactions like click or
 * focus that can cause amCharts to override the fill on individual legend items.
 *
 * @param {object} legend - amCharts Legend object to repaint
 * @param {object} textColorAm4 - amCharts color object for text/label fill
 * @param {object} bgColorAm4 - amCharts color object for focused item background fill
 * @returns {void}
 */
const refreshRenderedLegendItemsForTheme = (legend, textColorAm4, bgColorAm4) => {
  if (!legend || legend.isDisposed?.()) return;

  // Keep template-level styling (for future items)
  if (legend.labels?.template) legend.labels.template.fill = textColorAm4;
  if (legend.itemContainers?.template) legend.itemContainers.template.fill = textColorAm4;

  // Repaint already-rendered legend items (important after click/focus interaction)
  if (legend.itemContainers) {
    legend.itemContainers.each((itemContainer) => {
      if (!itemContainer || itemContainer.isDisposed?.()) return;

      const dataItem = itemContainer.dataItem;
      if (dataItem?.label && !dataItem.label.isDisposed?.()) {
        dataItem.label.fill = textColorAm4;
      }

      // If legend item has active/focus background, retint it for current theme
      if (
        itemContainer.background &&
        !itemContainer.background.isDisposed?.() &&
        itemContainer.background.fillOpacity > 0
      ) {
        itemContainer.background.fill = bgColorAm4;
      }
    });
  }
};

const legendFocusIndicatorBottomTrimmed = ["circuit-usage", "circuit-usage-trend-chart"];
const legendFocusIndicatorTopTrimmed = ["app360_at_site_circuits"];
const nonClickableLegends = ["Loss", "Latency", "Jitter", "Baseline", "Available", "Unavailable"];

const getLegendName = (target) =>{
  return target?.dataItem?.dataContext?.name || target?.dataItem?.name || "";
}

/**
 * Disables click/toggle interactivity on a legend item when it should be non-interactive.
 * A legend item is disabled if it belongs to the static non-clickable series list
 * (e.g., Loss, Latency, Jitter), if it is an anomaly-detection legend item, or if it
 * is the "Usage" series on any chart other than "usage-circuits-charts".
 * Focusability is preserved so keyboard users can still tab to and read the item.
 *
 * @param {object} target - amCharts legend item container to configure
 * @param {boolean} isLegendNameAnomalyDetected - True when the legend belongs to the anomaly legend
 * @param {object} props - Chart props; uses `props.id` to identify the chart instance
 * @returns {void}
 */
const setLegendItemInteractivity = (target, isLegendNameAnomalyDetected, props) => {
  const legendName = getLegendName(target);
  const shouldDisable = isLegendNameAnomalyDetected || nonClickableLegends.includes(legendName) || ((legendName === "Usage" && props?.id !== "usage-circuits-charts") ? true : false);

  if (shouldDisable) {
    target.clickable = false;
    target.interactionsEnabled = false;
    target.togglable = false;
    target.focusable = true;
  }
};

/**
 * Attaches one-time legend interaction handlers and non-clickable adapters.
 *
 * This function configures legend items so specific series remain non-toggleable,
 * applies focus/blur visual treatment for keyboard navigation, and registers cleanup
 * disposers when the legend is destroyed. It is intentionally idempotent and guarded
 * by internal flags so repeated theme applications do not duplicate listeners.
 *
 * @param {object} legend - amCharts Legend instance whose item container template is updated
 * @param {object} am4core - amCharts core module used to create theme-aware colors
 * @param {object} props - Chart props; uses `props.id` for chart-specific focus indicator behavior
 * @param {boolean} [isLegendNameAnomalyDetected=false] - Marks this as anomaly legend handling
 * @returns {void}
 */
const attachLegendInteractionHandlers = (legend, am4core, props, isLegendNameAnomalyDetected=false) => {
  if (!legend?.itemContainers || legend.isDisposed?.()) return;

  const template = legend.itemContainers.template;
  if (!template || template.isDisposed?.()) return;

  legend.itemContainers.each((item) => {
    setLegendItemInteractivity(item, isLegendNameAnomalyDetected, props);
});

  if (!legend._nonClickableAdaptersAttached) {
    legend._nonClickableAdaptersAttached = true;

    template.adapter.add("clickable", (value, target) => {
      const name = getLegendName(target);
      return isLegendNameAnomalyDetected || nonClickableLegends.includes(name) || (name === "Usage" && props?.id !== "usage-circuits-charts") ? false : value;
    });

    template.adapter.add("interactionsEnabled", (value, target) => {
      const name = getLegendName(target);
      return isLegendNameAnomalyDetected || nonClickableLegends.includes(name) || (name === "Usage" && props?.id !== "usage-circuits-charts")? false : value;
    });

    template.adapter.add("togglable", (value, target) => {
      const name = getLegendName(target);
      return isLegendNameAnomalyDetected || nonClickableLegends.includes(name) || (name === "Usage" && props?.id !== "usage-circuits-charts") ? false : value;
    });

    // Optional hard-stop in case another place re-enables interactivity
    template.events.on("hit", (ev) => {
      const name = getLegendName(ev?.target);
      if (isLegendNameAnomalyDetected || nonClickableLegends.includes(name) || (name === "Usage" && props?.id !== "usage-circuits-charts")) {
        ev?.event?.stopImmediatePropagation?.();
      }
    });
  }

  isLegendNameAnomalyDetected ? template.paddingLeft = 12 : template.paddingLeft = 6;

  // IMPORTANT: only attach once
  if (legend._interactionHandlersAttached) return;
  legend._interactionHandlersAttached = true;

  legend._focusDisposer = safeOn(template, "focus", (ev) => {
    if (!ev.target || !ev.target.dataItem || ev.target.isDisposed?.()) return;
    if(legendFocusIndicatorBottomTrimmed.includes(props?.id))
    {
      const chartContainer = document.getElementById(props.id);
      if (chartContainer) chartContainer.style.height = "16rem";
    }

    if(legendFocusIndicatorTopTrimmed.includes(props?.id)){
      const chartContainer = document.getElementById(props.id);
      if (chartContainer){
        ev.target.dy = 2;
      }
    }

    ev.target.background.strokeWidth = 1;
    ev.target.background.fill = isDarkModeActive()
      ? am4core.color(colors.gray15)
      : am4core.color(colors.gray100);
    ev.target.background.fillOpacity = 1;
  });

  legend._blurDisposer = safeOn(template, "blur", (ev) => {
    if (!ev.target || !ev.target.dataItem || ev.target.isDisposed?.()) return;

    if(legendFocusIndicatorBottomTrimmed.includes(props?.id)) 
    {
      const chartContainer = document.getElementById(props.id);
      if (chartContainer) chartContainer.style.height = "15rem";
    }

    if(legendFocusIndicatorTopTrimmed.includes(props?.id)){
      const chartContainer = document.getElementById(props.id);
      if (chartContainer){
        ev.target.dy = 0;
      }
    }

    ev.target.background.strokeWidth = 0;
    ev.target.background.fillOpacity = 0;
  });

  legend._cleanupDisposer = safeOn(legend, "disposed", () => {
    legend._focusDisposer?.dispose?.();
    legend._blurDisposer?.dispose?.();
    legend._cleanupDisposer?.dispose?.();

    legend._focusDisposer = null;
    legend._blurDisposer = null;
    legend._cleanupDisposer = null;
    legend._interactionHandlersAttached = false;
  });
};

/**
 * Applies base label/background styling for scrollbar axis labels.
 *
 * This normalizes label visibility and ensures the label background exists
 * but remains visually transparent until a focused/active scrollbar state
 * explicitly changes it.
 *
 * @param {object} labels - amCharts axis label template from the scrollbar chart axis renderer
 * @param {object} am4core - amCharts core module used for color conversion
 * @returns {void}
 */
const applyScrollBarLabelBaseStyle = (labels, am4core) => {
  labels.fillOpacity = 1;
  labels.background.disabled = false;
  labels.background.fill = am4core.color(colors.gray100);
  labels.background.fillOpacity = 0;
  labels.background.opacity = 0;
  labels.disabled = false;
};

/**
 * Applies keyboard-focus styling to the chart scrollbar.
 *
 * This state increases visual emphasis for accessibility by adding a clear
 * background and border around the scrollbar and switching scrollbar axis
 * label text to high-contrast dark text.
 *
 * @param {object} scrollBar - amCharts scrollbar instance to style in focus state
 * @param {object} colors - Shared color token map used by chart theme utilities
 * @param {object} am4core - amCharts core module used for color conversion
 * @returns {void}
 */
const applyTabFocusState = (scrollBar, colors, am4core) => {
  // Set white background instead of transparent
  scrollBar.background.fill = am4core.color(colors.gray100);
  scrollBar.background.fillOpacity = 1;
  scrollBar.background.stroke = am4core.color(colors.gray0);
  scrollBar.background.strokeWidth = 2;

  // Change text color inside scrollbar (axis labels)
  scrollBar.scrollbarChart.xAxes.each((axis) => {
    const labels = axis.renderer.labels.template;
    labels.fill = am4core.color(colors.gray0);
    applyScrollBarLabelBaseStyle(labels, am4core);
  });
}

// Shared function for default/hover state
const applyActiveState = (scrollBar, colors, am4core) => {
  // Change text color inside scrollbar (axis labels)
  scrollBar.scrollbarChart.xAxes.each((axis) => {
    const labels = axis.renderer.labels.template;
    labels.fill = isDarkModeActive() ? am4core.color(colors.gray100) : am4core.color("#000000");
    labels.fillOpacity = 1;
    labels.opacity = 1;  // Force full opacity on the label itself
    labels.strokeOpacity = 0;
    labels.stroke = am4core.color(colors.gray0);
    applyScrollBarLabelBaseStyle(labels, am4core);
    labels.hidden = false;
    labels.invalidate();
  });
};

// Shared function for blur/mouseout state
const applyInactiveState = (scrollBar, colors, am4core) => {
  scrollBar.background.fill = am4core.color(colors.blue45);
  scrollBar.background.fillOpacity = 0.5;
  scrollBar.background.strokeWidth = 0;
  // Reset label backgrounds
  scrollBar.scrollbarChart.xAxes.each((axis) => {
    const labels = axis.renderer.labels.template;
    labels.fill = isDarkModeActive() ? am4core.color(colors.gray100) : am4core.color(colors.gray0);
    labels.fillOpacity = 1;
  });

  // Reset scrollbar chart background
  scrollBar.scrollbarChart.background.fillOpacity = 0;
};

/**
 * Routes scrollbar interaction events to the matching visual state handler.
 *
 * Supported event types are `focus`, `over`, `blur`, and `out`, which map to
 * focused, active/hover, and inactive styling respectively.
 *
 * @param {object} event - amCharts interaction event object containing the `type`
 * @param {object} scrollBar - amCharts scrollbar instance whose styles are updated
 * @param {object} colors - Shared color token map used by chart theme utilities
 * @param {object} am4core - amCharts core module used for color conversion
 * @returns {void}
 */
const handleCurrentScrollBarState = (event, scrollBar, colors, am4core) => {
  switch (event.type) {
    case "focus":
      applyTabFocusState(scrollBar, colors, am4core);
      break;
    case "over":
      applyActiveState(scrollBar, colors, am4core);
      break;
    case "blur":  
    case "out":
      applyInactiveState(scrollBar, colors, am4core);
      break;
  }
}

/**
 * Applies theme-aware colors to all amCharts chart elements (labels, axes, legends).
 * Updates axis labels, titles, legend text, and grid lines to use proper text colors
 * for visibility and contrast in both light and dark modes.
 * 
 * @param {object} chart - amCharts XYChart object
 * @param {object} am4core - amCharts core module (for color conversion)
 * @returns {void}
 */
export const applyChartDarkTheme = (chart, am4core, props) => {
  if (!chart || !am4core) return;
  
  const isDark = isDarkModeActive();
  const textColor = isDark ? colors.gray100 : colors.gray15;
  const bgColor = isDark ? colors.gray15 : colors.gray100;
  const textColorAm4 = am4core.color(textColor);
  const bgColorAm4 = am4core.color(bgColor);
  const gridOpacity = isDark ? 0.15 : 0.1;
  const axisLineColor = isDark ? colors.gray100 : colors.gray15;
  const axisLineColorAm4 = am4core.color(axisLineColor);
  
  // Apply color to chart title
  if (chart.titles && chart.titles.length > 0) {
    chart.titles.each(title => {
      title.fill = textColorAm4;
    });
  }
  
  // Apply colors to X axes
  if (chart.xAxes && chart.xAxes.length > 0) {
    chart.xAxes.each(axis => {
      if (axis.renderer?.labels) {
        axis.renderer.labels.template.fill = textColorAm4;
      }
      if (axis.title) {
        axis.title.fill = textColorAm4;
      }
      if (axis.renderer?.grid) {
        axis.renderer.grid.template.strokeOpacity = gridOpacity;
        axis.renderer.grid.template.stroke = axisLineColorAm4;
      }
      if (axis.renderer?.line) {
        axis.renderer.line.stroke = axisLineColorAm4;
      }
    });
  }
  
  // Apply colors to Y axes
  if (chart.yAxes && chart.yAxes.length > 0) {
    chart.yAxes.each(axis => {
      if (axis.renderer?.labels) {
        axis.renderer.labels.template.fill = textColorAm4;
      }
      if (axis.title) {
        axis.title.fill = textColorAm4;
      }
      if (axis.renderer?.grid) {
        axis.renderer.grid.template.strokeOpacity = gridOpacity;
        axis.renderer.grid.template.stroke = axisLineColorAm4;
      }
      if (axis.renderer?.line) {
        axis.renderer.line.stroke = axisLineColorAm4;
      }
    });
  }
  
  // Apply colors to legend
  if (chart.legend) {
    chart.legend.labels.template.fill = textColorAm4;
    refreshRenderedLegendItemsForTheme(chart.legend, textColorAm4, bgColorAm4);
    if (chart.legend.itemContainers) {
      chart.legend.itemContainers.template.fill = textColorAm4;
      refreshRenderedLegendItemsForTheme(chart.legend, textColorAm4, bgColorAm4);
    }
    const legend = chart.legend;
    if (legend?.itemContainers && !legend.isDisposed?.()) {
      legend.labels.template.fill = textColorAm4;
      refreshRenderedLegendItemsForTheme(chart.legend, textColorAm4, bgColorAm4);

      // attach once; subsequent calls only update fill above
      attachLegendInteractionHandlers(legend, am4core, props);
    }
  }
  
  // Apply colors to anomaly legend (if it exists)
  if (chart._anomalyLegend) {
    chart._anomalyLegend.labels.template.fill = textColorAm4;
    refreshRenderedLegendItemsForTheme(chart._anomalyLegend, textColorAm4, bgColorAm4);
    if (chart._anomalyLegend?.itemContainers && !chart._anomalyLegend.isDisposed?.()) {
      const anomalyLegend = chart._anomalyLegend;

      // attach once; subsequent calls only update fill above
      attachLegendInteractionHandlers(anomalyLegend, am4core, props, true);
    }
  }
  
  // Apply colors to cursor lines and tooltip
  if (chart.cursor) {
    // Style cursor crosshair lines
    if (chart.cursor.lineX) {
      chart.cursor.lineX.stroke = textColorAm4;
      chart.cursor.lineX.strokeOpacity = isDark ? 0.5 : 0.3;
    }
    if (chart.cursor.lineY) {
      chart.cursor.lineY.stroke = textColorAm4;
      chart.cursor.lineY.strokeOpacity = isDark ? 0.5 : 0.3;
    }
    // Style cursor tooltip
    if (chart.cursor.behavior !== 'none' && chart.cursor.xAxis?.tooltip) {
      chart.cursor.xAxis.tooltip.label.fill = textColorAm4;
    }
  }
  
  // Apply colors to series tooltips
  if (chart.series && chart.series.length > 0) {
    chart.series.each(series => {
      if (series.tooltip) {
        series.tooltip.getFillFromObject = false;
        series.tooltip.background.fill = bgColorAm4;
        series.tooltip.autoTextColor = false;
        series.tooltip.label.fill = textColorAm4;
      }
    });
  }

  if(chart.scrollbarX && !chart.scrollbarX.isDisposed?.()){
      const scrollBar = chart.scrollbarX;
       if (!scrollBar._interactionHandlersAttached){
         scrollBar._interactionHandlersAttached = true;

         scrollBar._focusDisposer = safeOn(scrollBar, "focus", (event) =>
           handleCurrentScrollBarState(event, scrollBar, colors, am4core)
         );
         scrollBar._blurDisposer = safeOn(scrollBar, "blur", (event) =>
           handleCurrentScrollBarState(event, scrollBar, colors, am4core)
         );
         scrollBar._overDisposer = safeOn(scrollBar, "over", (event) =>
           handleCurrentScrollBarState(event, scrollBar, colors, am4core)
         );
         scrollBar._outDisposer = safeOn(scrollBar, "out", (event) =>
           handleCurrentScrollBarState(event, scrollBar, colors, am4core)
         );

         // cleanup on dispose
         scrollBar._cleanupDisposer = safeOn(scrollBar, "disposed", () => {
           scrollBar._focusDisposer?.dispose?.();
           scrollBar._blurDisposer?.dispose?.();
           scrollBar._overDisposer?.dispose?.();
           scrollBar._outDisposer?.dispose?.();
           scrollBar._cleanupDisposer?.dispose?.();
           scrollBar._interactionHandlersAttached = false;
         });
       }
       applyActiveState(scrollBar, colors, am4core);
  }
};

/**
 * Applies theme-aware colors to an amCharts legend and sets up dynamic theme change observer.
 * Updates legend label colors based on current theme and creates a MutationObserver
 * to automatically update colors when the theme changes.
 * 
 * @param {object} legend - amCharts Legend object
 * @param {object} am4core - amCharts core module (for color conversion)
 * @returns {void}
 */
export const applyLegendTheme = (legend, am4core) => {
  if (!legend || !am4core) return;
  
  // Helper function to update legend colors
  const updateLegendColors = () => {
    const legendTextColor = isDarkModeActive() ? colors.gray100 : colors.gray15;
    legend.labels.template.fill = am4core.color(legendTextColor);
  };
  
  // Apply initial theme colors
  updateLegendColors();
  
  // Setup theme observer for dynamic updates when theme changes
  const legendThemeObserver = createThemeChangeObserver(updateLegendColors);
  
  // Store observer on legend object for cleanup if needed
  legend._themeObserver = legendThemeObserver;
};

export const applyMapDarkTheme = (el) => {
  setTimeout(() => {
    if (el.parentElement) {
     el.parentElement.style.background = getCurrentTheme() ==='dark' ? colors.gray0 : colors.gray100; 
    } 
    const borderColor = getCurrentTheme() ==='dark' ? colors.gray0 : colors.gray100;
    let style = document.getElementById('geo-tooltip-arrow-after-style');
    if (!style) {
      style = document.createElement('style');
      style.id = 'geo-tooltip-arrow-after-style';
      document.head.appendChild(style);
    }
    style.innerHTML = `.geo-fw--styles-styles_less--tooltip-arrow::after { border-right-color: ${borderColor} !important; }`;
  }, 0);
}

/**
 * 
 * @param {object} chart - amCharts chart object (XYChart, SankeyDiagram, etc.)
 * @param {object} am4core - amCharts core module (for color conversion)
 * @returns {void}
 * 
 * @example
 * // In any chart component:
 * const chart = am4core.create(chartId, am4charts.XYChart);
 * // ... configure chart ...
 * initChartThemeSupport(chart, am4core);
 */
export const initChartThemeSupport = (chart, am4core, props={}) => {
  if (!chart || !am4core) return;
  
  // Apply dark theme colors to chart elements
  applyChartDarkTheme(chart, am4core, props);
  
  // Setup theme observer for dynamic updates when theme changes
  const themeObserver = createThemeChangeObserver(() => {
    applyChartDarkTheme(chart, am4core, props);
  });
  
  // Cleanup observer when chart is disposed
  chart.events.on('disposed', () => themeObserver.disconnect());
};
